/** Visual bar templates — design presets for the Templates tab. */

export const DEFAULT_TEMPLATE_ID = "minimal_white";

const BASE_TYPO = {
  font: "theme",
  message: { size: 16, color: "#202223", visible: true },
  product_title: { size: 16, color: "#202223" },
  product_price: { size: 14, color: "#202223" },
  compare_price: { size: 14, color: "#878E99" },
};

const BASE_BUTTON = {
  border_color: "#000000",
  text_size: 14,
  text_color: "#000000",
  corner_radius: 4,
};

/** Upsell colors for light card backgrounds. */
function upsellOnLight(messageColor = "#202223") {
  return {
    typography: {
      message: { size: 16, color: messageColor },
      product_title: { size: 16, color: "#202223" },
      product_price: { size: 14, color: "#202223" },
      compare_price: { size: 14, color: "#878E99" },
    },
    button: { border_color: "#000000", text_color: "#000000", text_size: 14, corner_radius: 4 },
    icon: { arrow_color: "#6B7280" },
  };
}

/** Upsell colors for dark card backgrounds — readable on black/navy bars. */
function upsellOnDark(accent = "#FFFFFF", messageColor = "#FFFFFF") {
  return {
    typography: {
      message: { size: 16, color: messageColor },
      product_title: { size: 16, color: "#FFFFFF" },
      product_price: { size: 14, color: accent === "#FFFFFF" ? "#F3F4F6" : accent },
      compare_price: { size: 14, color: "#9CA3AF" },
    },
    button: {
      border_color: accent,
      text_color: "#111827",
      text_size: 14,
      corner_radius: 4,
    },
    icon: { arrow_color: "#E5E7EB" },
  };
}

/** Upsell colors matched to a colored (non-dark) theme. */
function upsellThemed({ message, title, price, compare, btnBorder, btnText, arrow }) {
  return {
    typography: {
      message: { size: 16, color: message || title },
      product_title: { size: 16, color: title },
      product_price: { size: 14, color: price || title },
      compare_price: { size: 14, color: compare || "#878E99" },
    },
    button: {
      border_color: btnBorder || title,
      text_color: btnText || title,
      text_size: 14,
      corner_radius: 4,
    },
    icon: { arrow_color: arrow || compare || "#6B7280" },
  };
}

function makeDesign(templateId, overrides = {}) {
  const d = {
    template: templateId,
    card: {
      background_type: "solid",
      background: "#FFFFFF",
      gradient: { from: "#FFFFFF", to: "#F6F6F7", angle: 90 },
      background_pattern: null,
      background_image: null,
      corner_radius: 8,
      border_size: 1,
      border_color: "#E3E3E3",
      spacing: { inside_top: 16, inside_bottom: 16, outside_top: 20, outside_bottom: 20 },
    },
    progress_bar: {
      visible: true,
      background: "#E4E5E7",
      fill: "#000000",
      corner_radius: 4,
    },
    typography: { ...BASE_TYPO },
    button: { ...BASE_BUTTON },
    icon: { arrow_color: "#BBC0C4" },
    layout: { widget_width_percent: 100 },
  };
  return deepMerge(d, overrides);
}

function deepMerge(base, patch) {
  if (!patch || typeof patch !== "object") return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(patch).forEach((k) => {
    const pv = patch[k];
    const bv = out[k];
    if (pv && typeof pv === "object" && !Array.isArray(pv) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[k] = deepMerge(bv, pv);
    } else {
      out[k] = pv;
    }
  });
  return out;
}

/** @type {{ id: string, name: string, group: string, design: object }[]} */
export const BAR_DESIGN_TEMPLATES = [
  {
    id: "minimal_white",
    name: "Minimal white",
    group: "Classic",
    design: makeDesign("minimal_white"),
  },
  {
    id: "dark_teal",
    name: "Dark teal",
    group: "Classic",
    design: makeDesign("dark_teal", {
      card: {
        background: "#1a2332",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#2dd4bf" },
      ...upsellOnDark("#2dd4bf"),
    }),
  },
  {
    id: "black_gold",
    name: "Black & gold",
    group: "Classic",
    design: makeDesign("black_gold", {
      card: {
        background: "#000000",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#facc15" },
      ...upsellOnDark("#facc15"),
    }),
  },
  {
    id: "gradient_cyan_purple",
    name: "Cyan purple",
    group: "Classic",
    design: makeDesign("gradient_cyan_purple", {
      card: {
        background_type: "gradient",
        gradient: { from: "#22d3ee", to: "#a78bfa", angle: 90 },
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#1e3a5f" },
      ...upsellThemed({
        message: "#1e3a5f",
        title: "#1e3a5f",
        price: "#1e3a5f",
        compare: "#475569",
        btnBorder: "#1e3a5f",
        btnText: "#1e3a5f",
        arrow: "#334155",
      }),
    }),
  },
  {
    id: "sparkle_blue",
    name: "Sparkle blue",
    group: "Classic",
    design: makeDesign("sparkle_blue", {
      card: {
        background_type: "pattern",
        background: "#bfdbfe",
        background_pattern: "sparkle",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#000000" },
      ...upsellThemed({
        message: "#1e3a8a",
        title: "#1e3a8a",
        price: "#1e3a8a",
        compare: "#64748b",
        btnBorder: "#1e3a8a",
        btnText: "#1e3a8a",
        arrow: "#3b82f6",
      }),
    }),
  },
  {
    id: "hearts_pink",
    name: "Hearts pink",
    group: "Classic",
    design: makeDesign("hearts_pink", {
      card: {
        background_type: "pattern",
        background: "#fce7f3",
        background_pattern: "hearts",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#e11d48" },
      ...upsellThemed({
        message: "#be123c",
        title: "#9f1239",
        price: "#be123c",
        compare: "#9ca3af",
        btnBorder: "#be123c",
        btnText: "#be123c",
        arrow: "#e11d48",
      }),
    }),
  },
  {
    id: "vibrant_orange",
    name: "Vibrant orange",
    group: "Classic",
    design: makeDesign("vibrant_orange", {
      card: {
        background: "#f97316",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#9a3412" },
      ...upsellOnDark("#FFFFFF"),
    }),
  },
  {
    id: "halloween_spiderweb",
    name: "Halloween",
    group: "Seasonal",
    design: makeDesign("halloween_spiderweb", {
      card: {
        background_type: "pattern",
        background: "#0f172a",
        background_pattern: "spiderweb",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#3b82f6" },
      ...upsellOnDark("#93C5FD"),
    }),
  },
  {
    id: "christmas_holiday",
    name: "Christmas",
    group: "Seasonal",
    design: makeDesign("christmas_holiday", {
      card: {
        background_type: "pattern",
        background: "#fef3c7",
        background_pattern: "christmas",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#d97706" },
      ...upsellThemed({
        message: "#92400e",
        title: "#78350f",
        price: "#92400e",
        compare: "#a8a29e",
        btnBorder: "#b45309",
        btnText: "#92400e",
        arrow: "#d97706",
      }),
    }),
  },
  {
    id: "mint_green",
    name: "Mint green",
    group: "Seasonal",
    design: makeDesign("mint_green", {
      card: {
        background: "#d1fae5",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#065f46" },
      ...upsellThemed({
        message: "#065f46",
        title: "#064e3b",
        price: "#065f46",
        compare: "#6b7280",
        btnBorder: "#047857",
        btnText: "#065f46",
        arrow: "#059669",
      }),
    }),
  },
  {
    id: "winter_snow",
    name: "Winter snow",
    group: "Seasonal",
    design: makeDesign("winter_snow", {
      card: {
        background_type: "pattern",
        background: "#7dd3fc",
        background_pattern: "snow",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#1e40af" },
      ...upsellThemed({
        message: "#1e3a8a",
        title: "#1e3a8a",
        price: "#1d4ed8",
        compare: "#64748b",
        btnBorder: "#1e40af",
        btnText: "#1e3a8a",
        arrow: "#3b82f6",
      }),
    }),
  },
  {
    id: "festive_lights",
    name: "Festive lights",
    group: "Seasonal",
    design: makeDesign("festive_lights", {
      card: {
        background_type: "pattern",
        background: "#374151",
        background_pattern: "lights",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#fbbf24" },
      ...upsellOnDark("#fbbf24"),
    }),
  },
  {
    id: "minimal_black",
    name: "Minimal black",
    group: "Seasonal",
    design: makeDesign("minimal_black", {
      card: {
        background: "#000000",
        border_size: 0,
        border_color: "transparent",
        corner_radius: 6,
      },
      progress_bar: { background: "#FFFFFF", fill: "#4b5563" },
      ...upsellOnDark("#FFFFFF"),
    }),
  },
];

const TEMPLATE_MAP = Object.fromEntries(BAR_DESIGN_TEMPLATES.map((t) => [t.id, t]));

export function getBarDesignTemplate(id) {
  return TEMPLATE_MAP[id] || TEMPLATE_MAP[DEFAULT_TEMPLATE_ID];
}

export function getDefaultBarDesign() {
  return JSON.parse(JSON.stringify(getBarDesignTemplate(DEFAULT_TEMPLATE_ID).design));
}

/** Apply a template preset; keeps widget width and Design-tab animation settings. */
export function applyBarDesignTemplate(templateId, currentDesign = {}) {
  const tpl = getBarDesignTemplate(templateId);
  const width = currentDesign?.layout?.widget_width_percent ?? 100;
  const next = JSON.parse(JSON.stringify(tpl.design));
  next.layout = { ...next.layout, widget_width_percent: width };
  if (currentDesign?.message_animation) {
    next.message_animation = JSON.parse(JSON.stringify(currentDesign.message_animation));
  } else {
    next.message_animation = { enabled: false };
  }
  return next;
}

export function resolveActiveTemplateId(design) {
  const id = design?.template;
  if (id && TEMPLATE_MAP[id]) return id;
  return DEFAULT_TEMPLATE_ID;
}

/** Ensure saved design always has a valid template id (legacy bars used "custom"). */
export function normalizeDesignTemplate(design) {
  if (!design || typeof design !== "object") {
    return { template: DEFAULT_TEMPLATE_ID };
  }
  const id = design.template;
  if (id && TEMPLATE_MAP[id]) return design;
  return { ...design, template: DEFAULT_TEMPLATE_ID };
}
